import { createListCollection, HStack } from '@chakra-ui/react';
import {
  DefaultUnitValueByType,
  getConversionFunctions,
  UnitTypeMap,
  UnitTypes,
  UnitValuesByType,
} from '@utils/unit.utils';
import { useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { CustomSelect } from '../select/Select';
import { InputNumber, InputNumberProps } from './InputNumber';

export type UnitNumberInputProps<U extends UnitTypes> = {
  unitType: U;
  value?: number | null;
  precision?: number;
  defaultUnit?: UnitTypeMap[U];
} & InputNumberProps;

export function UnitNumberInput<U extends UnitTypes>({
  value,
  unitType,
  defaultUnit,
  onChange,
  precision = 3,
  ...rest
}: Readonly<UnitNumberInputProps<U>>) {
  const { formatMessage } = useIntl();
  const numberInputId = useId();
  const selectId = useId();

  const { convert, convertBack } = useMemo(() => getConversionFunctions(unitType), [unitType]);
  const [unit, setUnit] = useState<UnitTypeMap[U]>(defaultUnit ?? DefaultUnitValueByType[unitType]);

  const displayValue = useMemo(() => {
    return convert(unit, value ?? undefined, precision);
  }, [convert, precision, unit, value]);

  const handleValueChange = (newValue?: number) => {
    if (newValue === undefined) {
      onChange?.(undefined);
      return;
    }

    const baseValue = convertBack(unit, newValue, precision);
    onChange?.(baseValue ?? undefined);
  };

  const items = createListCollection({
    items: UnitValuesByType[unitType].map((key) => ({
      label: formatMessage({ id: `units.${key}` }),
      value: key,
    })),
  });

  return (
    <HStack gap={2} width="100%">
      <InputNumber
        ids={{ input: `${numberInputId}-input` }}
        key={unit}
        flexShrink={0}
        value={displayValue}
        onChange={handleValueChange}
        {...rest}
      />
      <CustomSelect
        ids={{
          hiddenSelect: `${selectId}-select`,
        }}
        size="sm"
        value={unit}
        collection={items}
        width={unitType === UnitTypes.TIME ? '180px' : 'max-content'}
        onChange={(unit) => setUnit(unit as UnitTypeMap[U])}
      />
    </HStack>
  );
}
